<template>
  <div class="flex flex-column gap-3">
    <filters-modal
      :visible.sync="visibleFiltersModal"
      :current-filters.sync="filters"
      :event-types="eventTypes"
      :loading-event-types="loadingEventTypes"
    />

    <a-modal
      title="Old History"
      :visible="visibleOldHistoryModal"
      width="1100px"
      @cancel="visibleOldHistoryModal = false"
      :destroy-on-close="true"
    >
      <a-table
        size="small"
        :style="{ whiteSpace: 'pre' }"
        :columns="oldColumns"
        :data-source="oldHistory"
        :pagination="oldPagination"
        :scroll="{ y: 490 }"
        :rowKey="record => record.history_id"
        :rowClassName="getRowClassName"
        :loading="loadingOldHistory"
        @change="handleOldTableChange"
        ><span slot="old_changed_by" slot-scope="text, record">
          <div class="flex flex-column">
            <b v-if="record.changed_by">{{ formatName(record.changed_by) }}</b>
            <template>
              <span v-if="record.email">{{ record.email }}</span>
              <span v-else></span
            ></template>
          </div>
        </span>
        <span slot="old_changes" slot-scope="text, record">
          <div class="flex flex-column" v-for="item in formatOldChanges(record.changes)" :key="item.key">
            <div class="flex gap-2">
              <b class="w-fit">{{ item.title }}:</b>
              <span :style="{ textWrap: 'initial' }">{{ formatChangeValue(item.value) }}</span>
            </div>
          </div>
        </span>
      </a-table>

      <template slot="footer">
        <div class="flex justify-content-end">
          <a-button @click="visibleOldHistoryModal = false"> Close </a-button>
        </div>
      </template>
    </a-modal>

    <table-layout>
      <template #header>
        <h3 class="m-0">Changes History</h3>
      </template>

      <template #controls-left> <a-button @click="openOldHistory" type="primary"> Old History </a-button></template>

      <template #controls-right>
        <a-badge :count="countFilters">
          <a-button icon="filter" @click="visibleFiltersModal = true"> Filters </a-button>
        </a-badge>
        <a-button class="flex align-items-center justify-content-center" @click="loadData" title="Refresh Page">
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </template>

      <template #filters>
        <active-table-filters :items="activeFilterTags" @edit="openFilterEditor" @remove="removeFilter" />
      </template>

      <a-table
        size="small"
        :style="{ whiteSpace: 'pre' }"
        :columns="columns"
        :data-source="history"
        :pagination="pagination"
        :scroll="{ y: 490, x: 1040 }"
        :rowKey="record => record.id"
        :rowClassName="getRowClassName"
        :loading="loading"
        @change="handleTableChange"
      >
        <span slot="old_value" slot-scope="text, record">
          <span :style="{ textWrap: 'initial' }">{{ formatHistoryValue(record.old_value) }}</span>
        </span>
        <span slot="new_value" slot-scope="text, record">
          <span :style="{ textWrap: 'initial' }">{{ formatHistoryValue(record.new_value) }}</span>
        </span>
      </a-table>
    </table-layout>
  </div>
</template>

<script>
import { Badge, Button, Modal, notification, Table } from 'ant-design-vue'
import { Icon } from '@iconify/vue2'
import { createNamespacedHelpers } from 'vuex'
import util from '@/util'
import tableMixin from '@/mixins/tableMixin'
import FiltersModal from './profile-view-changes-history-filters.vue'

const {
  mapState: profileState,
  mapActions: profileActions,
  mapMutations: profileMutations
} = createNamespacedHelpers('profile')

const filtersBaseState = Object.freeze({
  event_types: [],
  start_date: undefined,
  end_date: undefined
})

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-badge': Badge,
    'a-modal': Modal,
    'a-icon': Icon,
    'filters-modal': FiltersModal
  },
  mixins: [tableMixin],
  data() {
    return {
      loading: false,
      loadingOldHistory: false,
      loadingEventTypes: false,
      history: [],
      eventTypes: [],
      filters: { ...filtersBaseState },
      visibleFiltersModal: false,
      visibleOldHistoryModal: false,
      oldHistoryLoaded: false,
      oldPagination: {
        pageSizeOptions: ['10', '50', '100'],
        pageSize: 50,
        current: 1,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `Total: ${total}`
      },
      columns: [
        {
          title: 'ID',
          key: 'id',
          dataIndex: 'id',
          width: 90,
          align: 'center',
          defaultSortOrder: 'descend',
          sorter: (a, b) => (a.id ?? Number.MAX_SAFE_INTEGER) - (b.id ?? Number.MAX_SAFE_INTEGER),
          customRender: (_, record) => record.id ?? '-'
        },
        {
          title: 'Created At',
          key: 'created_at',
          width: 170,
          sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
          customRender: (_, record) => this.formatDateTime(record.created_at)
        },
        {
          title: 'Field',
          key: 'field',
          width: 220,
          customRender: (_, record) => record.field?.label ?? record.field?.name ?? '-',
          sorter: (a, b) => this.compareStrings(this.getFieldLabel(a), this.getFieldLabel(b))
        },
        {
          title: 'Author',
          key: 'author',
          width: 220,
          customRender: (_, record) => this.formatAuthor(record.author),
          sorter: (a, b) => this.compareStrings(this.formatAuthor(a.author), this.formatAuthor(b.author))
        },
        {
          title: 'Old Value',
          key: 'old_value',
          width: 240,
          scopedSlots: { customRender: 'old_value' }
        },
        {
          title: 'New Value',
          key: 'new_value',
          width: 240,
          scopedSlots: { customRender: 'new_value' }
        }
      ],
      oldColumns: [
        {
          title: 'ID',
          key: 'history_id',
          dataIndex: 'history_id',
          width: 90,
          align: 'center',
          defaultSortOrder: 'descend',
          sorter: (a, b) => (a.history_id ?? Number.MAX_SAFE_INTEGER) - (b.history_id ?? Number.MAX_SAFE_INTEGER),
          customRender: (_, record) => record.history_id ?? '-'
        },
        {
          title: 'Changed By',
          width: '300px',
          key: 'changed_by',
          scopedSlots: { customRender: 'old_changed_by' },
          sorter: (a, b) => {
            const aUsername = a.changed_by?.username
            const bUsername = b.changed_by?.username

            return aUsername == null ? -1 : bUsername == null ? 1 : aUsername.localeCompare(bUsername)
          }
        },
        {
          title: 'Date',
          key: 'date',
          width: 150,
          sorter: (a, b) => a.history_id - b.history_id,
          customRender: (_, record) => this.formatDateTime(record.date)
        },
        {
          title: 'Changes',
          key: 'changes',
          scopedSlots: { customRender: 'old_changes' }
        }
      ]
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate,
      oldHistory: state => state.associateChangesHistory
    }),
    projectTimeZone() {
      return this.$store.state.applicationState.timeZone
    },
    tableFilterLabels() {
      return {
        event_types: 'Event Types',
        start_date: 'Start Date',
        end_date: 'End Date'
      }
    },
    tableFilterEmptyValues() {
      return {
        event_types: []
      }
    },
    tableResetFilters() {
      return { ...filtersBaseState }
    }
  },
  watch: {
    associate: {
      handler(newVal) {
        this.resetLocalState()

        if (newVal?.id) {
          this.loadEventTypes()
          this.loadData()
        }
      },
      immediate: true
    },
    filters: {
      handler(newVal, prevVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(prevVal)) {
          this.pagination.current = 1
          this.loadData()
        }
      },
      deep: true
    }
  },
  methods: {
    ...profileActions(['getProfileHistory', 'getProfileNewHistory', 'getProfileNewHistoryEventTypes']),
    ...profileMutations(['SET_ASSOCIATE_CHANGES_HISTORY']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    resetLocalState() {
      this.history = []
      this.filters = { ...filtersBaseState }
      this.oldHistoryLoaded = false
      this.visibleOldHistoryModal = false
      this.SET_ASSOCIATE_CHANGES_HISTORY([])
      this.pagination.current = 1
      this.pagination.total = 0
      this.oldPagination.current = 1
      this.oldPagination.total = 0
    },
    formatDateTime(dateTime) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(dateTime, 'MM/DD/YYYY hh:mm A') ?? '-'
    },
    formatOldChanges(changes) {
      return Object.entries(changes ?? {}).map(([key, value]) => ({ ...value, key }))
    },
    formatChangeValue(value) {
      return util.convertChangesHistoryValueToProjectTimeZone(value, this.projectTimeZone)
    },
    formatHistoryValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      if (Array.isArray(value) || typeof value === 'object') {
        return JSON.stringify(value, null, 2)
      }

      return this.formatChangeValue(String(value))
    },
    formatAuthor(author) {
      if (!author) {
        return 'System'
      }

      const name = [author.first_name, author.last_name].filter(Boolean).join(' ')

      return name || 'System'
    },
    getFieldLabel(record) {
      return record.field?.label ?? record.field?.name ?? ''
    },
    compareStrings(a, b) {
      return a == null ? -1 : b == null ? 1 : String(a).localeCompare(String(b))
    },
    formatName(user) {
      return [user.first_name, user.last_name].filter(Boolean).join(' ')
    },
    buildParams() {
      const params = {}

      if (this.filters.event_types?.length) {
        params.event_types = this.filters.event_types.join(',')
      }

      if (this.filters.start_date) {
        params.date_from = this.filters.start_date
      }

      if (this.filters.end_date) {
        params.date_to = this.filters.end_date
      }

      return params
    },
    getErrorMessage(error) {
      const data = error?.response?.data

      if (typeof data === 'string') {
        return data
      }

      if (data && typeof data === 'object') {
        const messages = Object.values(data)
          .flatMap(value => (Array.isArray(value) ? value : [value]))
          .filter(Boolean)

        if (messages.length) {
          return messages.join('\n')
        }
      }

      return (typeof error === 'string' ? error : error?.message) || 'An error occurred while loading changes history.'
    },
    async loadEventTypes() {
      this.loadingEventTypes = true

      try {
        this.eventTypes = await this.getProfileNewHistoryEventTypes()
      } catch (error) {
        this.showNotification('error', 'Error', this.getErrorMessage(error))
      } finally {
        this.loadingEventTypes = false
      }
    },
    async loadData() {
      if (!this.associate?.id) {
        return
      }

      this.loading = true

      try {
        this.history = await this.getProfileNewHistory({
          id: this.associate.id,
          params: this.buildParams()
        })

        this.pagination.total = this.history.length
      } catch (error) {
        this.showNotification('error', 'Error', this.getErrorMessage(error))
      } finally {
        this.loading = false
      }
    },
    async openOldHistory() {
      this.visibleOldHistoryModal = true

      if (this.oldHistoryLoaded || !this.associate?.id) {
        return
      }

      this.loadingOldHistory = true

      try {
        await this.getProfileHistory({
          id: this.associate.id
        })

        this.oldPagination.total = this.oldHistory.length
        this.oldHistoryLoaded = true
      } catch (error) {
        this.showNotification('error', 'Error', this.getErrorMessage(error))
      } finally {
        this.loadingOldHistory = false
      }
    },
    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }
    },
    handleOldTableChange(pagination) {
      this.oldPagination = { ...pagination }
    }
  }
}
</script>
